import {
  collection,
  doc,
  getDoc,
  addDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  setDoc,
  query,
  where,
  serverTimestamp,
  increment,
  arrayUnion,
} from "firebase/firestore";
import { db } from "./firebase";
import { writeUserNotification } from "./userNotifications";
import { checkAndAutoFinalize } from "./calls";

// ── Admin: create / manage ────────────────────────────────────────────────────

export async function createBroadcast({ type, title, body, programTag, options, allowMultiple, xpReward, adminUid, callId, targetType, targetUserIds }) {
  return await addDoc(collection(db, "broadcasts"), {
    type,           // "announcement" | "question" | "poll" | "call_poll"
    title: title.trim(),
    body: body.trim(),
    programTag: programTag || "",
    options: options || [],
    allowMultiple: !!allowMultiple,
    xpReward: Number(xpReward || 0),
    isActive: true,
    createdAt: serverTimestamp(),
    createdBy: adminUid,
    targetType: targetType || "program",
    targetUserIds: targetUserIds || [],
    ...(callId ? { callId } : {}),
  });
}

export async function deactivateBroadcast(broadcastId) {
  await updateDoc(doc(db, "broadcasts", broadcastId), { isActive: false });
}

export async function updateBroadcast(broadcastId, { title, body, options, allowMultiple, xpReward }) {
  await updateDoc(doc(db, "broadcasts", broadcastId), {
    title: title.trim(),
    body: body.trim(),
    options: options || [],
    allowMultiple: !!allowMultiple,
    xpReward: Number(xpReward || 0),
  });
}

export async function deleteBroadcast(broadcastId) {
  await deleteDoc(doc(db, "broadcasts", broadcastId));
}

export async function getBroadcastsForProgram(programTag) {
  const snap = await getDocs(
    query(collection(db, "broadcasts"), where("programTag", "==", programTag))
  );
  const items = snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter(Boolean);
  items.sort((a, b) => {
    const at = a.createdAt?.toDate?.()?.getTime?.() || 0;
    const bt = b.createdAt?.toDate?.()?.getTime?.() || 0;
    return bt - at;
  });
  return items;
}

export async function getAllBroadcasts() {
  const snap = await getDocs(collection(db, "broadcasts"));
  const items = snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter(Boolean);
  items.sort((a, b) => {
    const at = a.createdAt?.toDate?.()?.getTime?.() || 0;
    const bt = b.createdAt?.toDate?.()?.getTime?.() || 0;
    return bt - at;
  });
  return items;
}

export async function getResponsesForBroadcast(broadcastId) {
  const snap = await getDocs(
    query(collection(db, "broadcastResponses"), where("broadcastId", "==", broadcastId))
  );
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

// ── Client: load & respond ────────────────────────────────────────────────────

export async function getActiveBroadcastsForUser(programTag, userId, dismissedBroadcasts = []) {
  // Fetch program-wide broadcasts + any broadcasts specifically targeting this user
  const [programSnap, targetedSnap] = await Promise.all([
    programTag
      ? getDocs(query(collection(db, "broadcasts"), where("programTag", "==", programTag)))
      : Promise.resolve({ docs: [] }),
    getDocs(query(collection(db, "broadcasts"), where("targetUserIds", "array-contains", userId))),
  ]);

  // Merge and deduplicate
  const seenIds = new Set();
  const allBroadcasts = [];
  for (const snap of [programSnap, targetedSnap]) {
    for (const d of snap.docs) {
      if (!seenIds.has(d.id)) {
        seenIds.add(d.id);
        allBroadcasts.push({ id: d.id, ...d.data() });
      }
    }
  }

  const broadcasts = allBroadcasts.filter((b) => {
    if (b.isActive === false) return false;
    // User-targeted broadcasts: only show to the targeted users
    if (b.targetType === "users") return (b.targetUserIds || []).includes(userId);
    return true;
  });

  // Get responses this user already submitted (avoids permission errors on
  // reading non-existent docs one-by-one).
  const responsesSnap = await getDocs(
    query(collection(db, "broadcastResponses"), where("userId", "==", userId))
  );
  const respondedIds = new Set(
    responsesSnap.docs.map((d) => d.data()?.broadcastId).filter(Boolean)
  );

  const dismissedSet = new Set(dismissedBroadcasts);
  const pending = broadcasts.filter((b) => !respondedIds.has(b.id) && !dismissedSet.has(b.id));

  pending.sort((a, b) => {
    const at = a.createdAt?.toDate?.()?.getTime?.() || 0;
    const bt = b.createdAt?.toDate?.()?.getTime?.() || 0;
    return at - bt; // oldest first on dashboard
  });

  return pending;
}

export async function submitBroadcastResponse({ broadcastId, broadcastTitle, userId, userName, userAvatar, type, comment, selections, starred, programTag, callId, options }) {
  await setDoc(doc(db, "broadcastResponses", `${broadcastId}_${userId}`), {
    broadcastId,
    broadcastTitle: broadcastTitle || "",
    userId,
    userName: userName || "",
    userAvatar: userAvatar || "",
    type,
    comment: comment || "",
    selections: selections || [],
    starred: starred || null,
    submittedAt: serverTimestamp(),
  });
  // If this is a call_poll, check whether all users have voted and auto-finalize
  if (type === "call_poll" && callId) {
    try {
      await checkAndAutoFinalize({ broadcastId, callId });
    } catch (_) {}
  }
}

export async function reactToResponse(responseId, emoji, { toUserId = "", broadcastTitle = "", fromName = "", fromAvatar = "" } = {}) {
  await updateDoc(doc(db, "broadcastResponses", responseId), {
    adminReaction: emoji,
    adminReactedAt: serverTimestamp(),
    // If client previously dismissed this thread, surface it again when coach adds new feedback
    clientDismissed: false,
    clientDismissedAt: null,
  });
  if (emoji && toUserId) {
    writeUserNotification({
      toUserId,
      type: "reaction",
      message: `${fromName || "Your coach"} reacted ${emoji} to your response${broadcastTitle ? ` on "${broadcastTitle}"` : ""}`,
      fromName,
      fromAvatar,
    });
  }
}

export async function replyToResponse(responseId, text, { toUserId = "", broadcastTitle = "", fromName = "", fromAvatar = "" } = {}) {
  await updateDoc(doc(db, "broadcastResponses", responseId), {
    adminReply: text.trim(),
    adminRepliedAt: serverTimestamp(),
    // If client previously dismissed this thread, surface it again when coach adds new feedback
    clientDismissed: false,
    clientDismissedAt: null,
  });
  if (text.trim() && toUserId) {
    writeUserNotification({
      toUserId,
      type: "comment",
      message: `${fromName || "Your coach"} replied to your response${broadcastTitle ? ` on "${broadcastTitle}"` : ""}`,
      fromName,
      fromAvatar,
    });
  }
}

export async function reactToAdminReply(responseId, emoji) {
  await updateDoc(doc(db, "broadcastResponses", responseId), {
    clientReplyReaction: emoji || null,
    clientReplyReactedAt: emoji ? serverTimestamp() : null,
  });
}

export async function replyBackToCoach(responseId, text) {
  await updateDoc(doc(db, "broadcastResponses", responseId), {
    clientReply: text.trim(),
    clientRepliedAt: serverTimestamp(),
  });
}

export async function dismissFeedbackForClient(responseId) {
  await updateDoc(doc(db, "broadcastResponses", responseId), {
    clientDismissed: true,
    clientDismissedAt: serverTimestamp(),
  });
}

// Returns all submitted responses for a user (for the conversations tab)
export async function getAllBroadcastResponsesForUser(userId) {
  const snap = await getDocs(
    query(collection(db, "broadcastResponses"), where("userId", "==", userId))
  );
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .sort((a, b) => {
      const at = a.submittedAt?.toDate?.()?.getTime?.() || 0;
      const bt = b.submittedAt?.toDate?.()?.getTime?.() || 0;
      return bt - at; // newest first
    });
}

// Returns all submitted responses for a user that have an admin reaction or reply,
// filtered to only broadcasts that still exist (not deleted).
export async function getSubmittedResponsesWithFeedback(userId) {
  const snap = await getDocs(
    query(collection(db, "broadcastResponses"), where("userId", "==", userId))
  );
  const responses = snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((r) => (r.adminReaction || r.adminReply) && !r.clientDismissed);

  if (responses.length === 0) return [];

  // Check which broadcasts still exist
  const uniqueBroadcastIds = [...new Set(responses.map((r) => r.broadcastId).filter(Boolean))];
  const broadcastChecks = await Promise.all(
    uniqueBroadcastIds.map((id) =>
      getDoc(doc(db, "broadcasts", id)).then((s) => ({ id, exists: s.exists() }))
    )
  );
  const existingIds = new Set(broadcastChecks.filter((b) => b.exists).map((b) => b.id));

  return responses.filter((r) => existingIds.has(r.broadcastId));
}

// Dismiss a broadcast without responding (persisted to user doc)
export async function dismissBroadcastForUser(broadcastId, userId) {
  await updateDoc(doc(db, "users", userId), {
    dismissedBroadcasts: arrayUnion(broadcastId),
  });
}

// Award XP to a user for responding to a broadcast (only if not already awarded)
export async function awardBroadcastXP(broadcastId, userId, xpReward, broadcastTitle) {
  const amount = Number(xpReward || 0);
  if (amount <= 0) return;
  // Mark xpAwarded on the response doc and increment user's xpAdjustmentTotal
  await updateDoc(doc(db, "broadcastResponses", `${broadcastId}_${userId}`), {
    xpAwarded: true,
    xpAwardedAt: serverTimestamp(),
  });
  await updateDoc(doc(db, "users", userId), {
    xpAdjustmentTotal: increment(amount),
  });
  await addDoc(collection(db, "users", userId, "xpAdjustments"), {
    amount,
    type: "earn",
    source: "broadcast",
    note: broadcastTitle ? `Broadcast: ${broadcastTitle}` : "Broadcast response",
    createdAt: serverTimestamp(),
  });
}
